import { isDifferential } from "@corca/core";
import { observer } from "mobx-react-lite";

import { Group } from "@/components/expression/utils/groups/Group";
import * as Node from "@/components/expression/utils/layout";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useNodeHover } from "@/components/expression/utils/useNodeHover";
import { EditNodeInput } from "@/components/input/EditNodeInput";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { isFraction } from "@/models/utils/semanticChecks";
import { twMerge } from "@/utils/twMerge";

import { bracketsFromSlice } from "./inline-node/wrapping/buildRowTree";
import { SymbolNode } from "./SymbolNode";
import { shouldOmitParentheses } from "./utils/functions";
import { NodeAttachments, useAttachmentShift } from "./utils/NodeAttachments";
import { nodeNestedHover } from "./utils/nodeNestedHover";

import type { ExpressionComponent } from "./types";
import type { GroupSlice } from "@/components/expression/inline-node/wrapping/types";
import type { PlaceholderVisible } from "@corca/data";
import type { ReactNode } from "react";

type Props = {
  slice?: GroupSlice;
  children?: ReactNode;
  symbolWrapper?: (symbol: ReactNode) => ReactNode;
};

export const FunctionNode: ExpressionComponent<"function", Props> = observer(function FunctionNode({
  node,
  className,
  slice = "whole",
  symbolWrapper,
  children,
}) {
  const { doc, symbolNode } = node;
  const { view, labelPlaceholderVisible } = useNodeStyle();
  const { isHovered } = useNodeHover(node);
  const { isHovered: isSymbolHovered, handlerProps: symbolHoverProps } = useNodeHover(
    symbolNode ?? node,
  );
  const { handlerProps, tooltip } = useTooltip({
    title: node.name,
    focusProps: {
      enabled: false,
    },
    hoverProps: {
      delay: {
        open: 1500,
      },
    },
  });

  const isFocused = doc.cursor.isOnNode(node);
  let omitParentheses = shouldOmitParentheses(node);

  const isTextView = view === "text";

  let placeholderVisible = labelPlaceholderVisible;
  let childrenPlaceholderVisible: PlaceholderVisible[] = [];

  if (isDifferential(node) && isFraction(node.parent?.parent)) {
    if (
      node.parent.parent.childrenIds[0] === node.parent.id &&
      node.parent.childrenIds.length === 1
    ) {
      const parentIsFocused = node.parent ? doc.cursor.isOnNode(node.parent) : false;
      const parentIsHovered = nodeNestedHover(node.parent?.parent);
      // hide differential placeholder when it's the only parameter of fraction numerator

      if (!(parentIsFocused || parentIsHovered || isFocused || isHovered)) {
        childrenPlaceholderVisible = ["none"];
      }
    }

    if (node.parent.parent.childrenIds[1] === node.parent.id) {
      omitParentheses = node.parameters.length === 1 && node.parameters[0].childrenIds.length <= 1;
    }
  }

  const hideParentheses = omitParentheses && !isFocused;

  if (placeholderVisible === "none" && (isFocused || isSymbolHovered)) {
    placeholderVisible = "if-empty";
  }

  const { shift, refs } = useAttachmentShift();

  const symbolComp = (
    <div
      {...handlerProps}
      {...symbolHoverProps}
      className={twMerge(
        "inline-flex",
        "pr-1 pl-0.5",
        view === "compact-expression" && "pr-0.5",
        (isTextView || (symbolNode?.isLargeOperator && hideParentheses)) && "px-0",
      )}
    >
      <EditNodeInput node={node} accept={["function"]} inputClassName="px-0">
        {symbolNode && (
          <NodeStyleProvider
            placeholderVisible={placeholderVisible}
            text={isHovered && !isTextView ? "primary" : undefined}
          >
            <SymbolNode node={symbolNode} style={{ marginTop: shift }} />
          </NodeStyleProvider>
        )}
      </EditNodeInput>
      {node.power && (
        <NodeAttachments
          {...refs}
          position="right"
          styleProviderValue={{ size: "xs" }}
          attachments={[
            {
              node: node.power.parameters[0],
              position: "right-top",
            },
          ]}
        />
      )}
    </div>
  );

  const symbol = symbolWrapper?.(symbolComp) ?? symbolComp;

  const { showLeft, showRight } = bracketsFromSlice(slice);
  const showPrefixSymbol = node.placement === "prefix" && showLeft;
  const showPostfixSymbol = node.placement === "postfix" && showRight;

  return (
    <>
      <Node.Container
        node={node}
        data-testid="function-node"
        className={twMerge(
          "print-color-economy",
          "inline-flex items-baseline px-0.5",
          isTextView && "px-0",
          (hideParentheses || !showRight) && "pr-0",
          !isTextView && !hideParentheses && showRight && "mr-[3px]",
          className,
        )}
        isHovered={isHovered}
      >
        {showPrefixSymbol && symbol}
        <Group
          node={node}
          parameters={node.parameters}
          openingSymbol={node.openingSymbol}
          closingSymbol={node.closingSymbol}
          disabled={omitParentheses}
          hideParentheses={hideParentheses}
          compensateBracketWidthRight={0}
          nodePlaceholders={{
            childrenVisible: childrenPlaceholderVisible,
          }}
          slice={slice}
        >
          {children}
        </Group>
        {showPostfixSymbol && symbol}
      </Node.Container>
      {tooltip}
    </>
  );
});
